import { useId } from 'react';

interface Props {
  readonly size?: number;
}

/**
 * Paradigm brand mark — the two overlapping spheres from the official
 * Paradigm logo (icon-only, no wordmark). Geometry ported from
 * `ui/paradex/src/assets/static/paradigm-logo-horizontal.svg`, trimmed to
 * the 52×52 icon viewport so it drops into the skills detail header at
 * any size without distortion.
 */
export default function ParadigmLogo({ size = 32 }: Props) {
  // Unique per instance so multiple logos (or another SVG reusing the id)
  // can't make `url(#…)` resolve to the wrong gradient.
  const gradientFront = useId();
  const gradientBack = useId();
  const overlayGradient = useId();
  const overlayMask = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 52 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradientFront}
          x1="4.26689"
          y1="13.5652"
          x2="47.4917"
          y2="17.9783"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#3D89FB" />
          <stop offset="1" stopColor="#5F49E4" stopOpacity="0.62" />
        </linearGradient>
        <radialGradient
          id={overlayGradient}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(-10.9775 39.5235) rotate(90) scale(24.2584 25.2653)"
        >
          <stop stopColor="#051323" />
          <stop offset="1" stopColor="#051323" stopOpacity="0" />
        </radialGradient>
        <linearGradient
          id={gradientBack}
          x1="16.1"
          y1="0"
          x2="61.5526"
          y2="4.66815"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#3D89FB" />
          <stop offset="1" stopColor="#5F49E4" stopOpacity="0.62" />
        </linearGradient>
        <mask
          id={overlayMask}
          maskUnits="userSpaceOnUse"
          x="0"
          y="13"
          width="38"
          height="39"
        >
          <path
            d="M18.8944 52C29.3295 52 37.7888 43.396 37.7888 32.7826C37.7888 22.1691 29.3295 13.5652 18.8944 13.5652C8.45931 13.5652 0 22.1691 0 32.7826C0 43.396 8.45931 52 18.8944 52Z"
            fill="white"
          />
        </mask>
      </defs>
      {/* Front (lower-left) sphere */}
      <path
        d="M18.8944 52C29.3295 52 37.7888 43.396 37.7888 32.7826C37.7888 22.1691 29.3295 13.5652 18.8944 13.5652C8.45931 13.5652 0 22.1691 0 32.7826C0 43.396 8.45931 52 18.8944 52Z"
        fill={`url(#${gradientFront})`}
      />
      {/* Soft shadow where the back sphere overlaps the front one — gives
          the mark its depth instead of two flat circles touching. */}
      <g mask={`url(#${overlayMask})`}>
        <path
          d="M50.7397 25.5427C50.7397 39.2155 39.8787 50.2994 26.4812 50.2994C13.0837 50.2994 2.2229 39.2155 2.2229 25.5427C2.2229 11.87 13.0837 0.786011 26.4812 0.786011C39.8787 0.786011 50.7397 11.87 50.7397 25.5427Z"
          fill={`url(#${overlayGradient})`}
          fillOpacity="0.5"
        />
      </g>
      {/* Back (upper-right) sphere */}
      <path
        d="M50.708 19.2175C50.708 29.8308 42.1041 38.4348 31.4906 38.4348C20.8771 38.4348 12.2732 29.8308 12.2732 19.2175C12.2732 8.60393 20.8771 0 31.4906 0C42.1041 0 50.708 8.60393 50.708 19.2175Z"
        fill={`url(#${gradientBack})`}
      />
    </svg>
  );
}
